import { useState } from "react";
import { SearchableChecklist, SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SiteSelectField } from "@/components/SiteSelectField";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

function toLocalInput(iso) {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function TripFormDialog({ trip, sites, rigs, employees, onClose, onSaved }) {
  const isEdit = !!trip;
  const [saving, setSaving] = useState(false);
  const [tripType, setTripType] = useState(trip?.trip_type || "Rig");
  const [rigAssetId, setRigAssetId] = useState(trip?.rig_asset_id || "");
  const [items, setItems] = useState(trip?.items?.length ? trip.items : [{ description: "", quantity: "" }]);
  const [fromSiteId, setFromSiteId] = useState(trip?.from_site_id || "");
  const [toFreeText, setToFreeText] = useState(!!trip?.to_site_free_text);
  const [toSiteId, setToSiteId] = useState(trip?.to_site_id || "");
  const [toSiteFreeText, setToSiteFreeText] = useState(trip?.to_site_free_text || "");
  const [finalDest, setFinalDest] = useState(trip?.final_destination_reference || "");
  const [startDate, setStartDate] = useState(toLocalInput(trip?.start_date));
  const [driverName, setDriverName] = useState(trip?.driver_name || "");
  const [driverPhone, setDriverPhone] = useState(trip?.driver_phone || "");
  const [accompanyingStaff, setAccompanyingStaff] = useState(trip?.accompanying_staff || []);
  const [transporterName, setTransporterName] = useState(trip?.transporter_name || "");
  const [agreedCost, setAgreedCost] = useState(trip?.agreed_transport_cost ?? "");

  const toggleStaff = (id) => setAccompanyingStaff((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const addItemRow = () => setItems((r) => [...r, { description: "", quantity: "" }]);
  const updateItemRow = (i, key, val) => setItems((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const removeItemRow = (i) => setItems((r) => r.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (tripType === "Rig" && !rigAssetId) return toast.error("Rig is required for a Rig-type Trip");
    if (tripType === "Material" && !items.some((r) => r.description.trim())) return toast.error("At least one Item is required for a Material-type Trip");
    if (!fromSiteId) return toast.error("From Site is required");
    if (!toFreeText && !toSiteId) return toast.error("To Site is required");
    if (toFreeText && !toSiteFreeText.trim()) return toast.error("Enter the destination site name");
    if (!finalDest.trim()) return toast.error("Final Destination Coordinates/Reference is required");
    if (agreedCost !== "" && parseFloat(agreedCost) < 0) return toast.error("Agreed Transport Cost cannot be negative");

    const payload = {
      trip_type: tripType,
      rig_asset_id: tripType === "Rig" ? rigAssetId : null,
      items: tripType === "Material" ? items.filter((r) => r.description.trim()) : [],
      from_site_id: fromSiteId,
      to_site_id: toFreeText ? null : toSiteId,
      to_site_free_text: toFreeText ? toSiteFreeText.trim() : null,
      final_destination_reference: finalDest.trim(),
      start_date: new Date(startDate).toISOString(),
      driver_name: driverName.trim() || null,
      driver_phone: driverPhone.trim() || null,
      accompanying_staff: accompanyingStaff,
      transporter_name: transporterName.trim() || null,
      agreed_transport_cost: agreedCost === "" ? null : parseFloat(agreedCost),
    };

    setSaving(true);
    try {
      const res = isEdit
        ? await client.put(`/transport-log/trips/${trip.id}`, payload)
        : await client.post("/transport-log/trips", payload);
      toast.success(isEdit ? "Trip updated" : "Trip created");
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="trip-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Trip — ${trip.trip_id}` : "Add Trip"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Trip Type</label>
              <Select value={tripType} onValueChange={setTripType} disabled={isEdit}>
                <SelectTrigger data-testid="trip-form-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rig" data-testid="trip-form-type-option-rig">
                    Rig
                  </SelectItem>
                  <SelectItem value="Material" data-testid="trip-form-type-option-material">
                    Material
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Start Date &amp; Time</label>
              <Input type="datetime-local" data-testid="trip-form-start-date-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
          </div>

          {tripType === "Rig" ? (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Rig *</label>
              <SearchableSelect testId="trip-form-rig-select" value={rigAssetId} onValueChange={setRigAssetId} placeholder="Select rig"
  options={rigs.map((r) => ({ value: r.asset_id, label: r.label, testId: `trip-form-rig-option-${r.asset_id}` }))} />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-slate-700">Items *</label>
                <Button type="button" size="sm" variant="outline" data-testid="trip-form-item-add" onClick={addItemRow}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {items.map((row, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      placeholder="Description"
                      className="flex-1"
                      data-testid={`trip-form-item-${i}-description`}
                      value={row.description}
                      onChange={(e) => updateItemRow(i, "description", e.target.value)}
                    />
                    <Input
                      placeholder="Quantity"
                      className="w-32"
                      data-testid={`trip-form-item-${i}-quantity`}
                      value={row.quantity || ""}
                      onChange={(e) => updateItemRow(i, "quantity", e.target.value)}
                    />
                    <button type="button" data-testid={`trip-form-item-${i}-remove`} onClick={() => removeItemRow(i)} className="text-slate-400 hover:text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">From Site *</label>
              <SiteSelectField sites={sites} value={fromSiteId} onChange={setFromSiteId} testId="trip-form-from-site-select" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">To Site *</label>
              {toFreeText ? (
                <Input
                  data-testid="trip-form-to-site-freetext"
                  placeholder="Destination site name"
                  value={toSiteFreeText}
                  onChange={(e) => setToSiteFreeText(e.target.value)}
                />
              ) : (
                <SiteSelectField sites={sites} value={toSiteId} onChange={setToSiteId} testId="trip-form-to-site-select" />
              )}
              <label className="flex items-center gap-2 text-xs text-slate-500 mt-1.5 cursor-pointer">
                <Checkbox data-testid="trip-form-to-site-freetext-toggle" checked={toFreeText} onCheckedChange={(v) => setToFreeText(!!v)} />
                Site not yet in Site Master
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Final Destination Coordinates/Reference *</label>
            <Input
              data-testid="trip-form-final-dest-input"
              value={finalDest}
              onChange={(e) => setFinalDest(e.target.value)}
              placeholder="Address / Plus Code / Lat,Long"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Driver Name</label>
              <Input data-testid="trip-form-driver-name-input" value={driverName} onChange={(e) => setDriverName(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Driver Phone</label>
              <Input data-testid="trip-form-driver-phone-input" value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Transporter Name</label>
              <Input data-testid="trip-form-transporter-name-input" value={transporterName} onChange={(e) => setTransporterName(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Agreed Transport Cost (₹)</label>
              <Input type="number" step="any" data-testid="trip-form-agreed-cost-input" value={agreedCost} onChange={(e) => setAgreedCost(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Accompanying Staff</label>
            <SearchableChecklist
              testId="trip-form-staff-list"
              searchPlaceholder="Search staff..."
              onToggle={toggleStaff}
              items={employees.map((e) => ({
                id: e.employee_id,
                searchText: `${e.employee_id} ${e.name}`,
                checked: accompanyingStaff.includes(e.employee_id),
                checkboxTestId: `trip-form-staff-checkbox-${e.employee_id}`,
                content: (
                  <>
                    <span className="font-mono">{e.employee_id}</span> — {e.name}
                  </>
                ),
              }))}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="trip-form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="trip-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Trip"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
